import { useState } from 'react';
import { toast } from 'sonner';

import { usePatchMatterMutation } from '@/services/api/mattersService';

interface UseInlineEditOptions<T> {
  matterId: string;
  initialValue: T;
  fieldName: string; // or mutation function
  onSuccessMessage?: string;
  transformValue?: (value: T) => any; // For payload formatting
  validate?: (value: T) => boolean | string; // Return true if valid, or error message string
}

export const useInlineEdit = <T>({
  matterId,
  initialValue,
  fieldName,
  onSuccessMessage = 'Updated successfully',
  transformValue,
  validate,
}: UseInlineEditOptions<T>) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<T>(initialValue);
  const [patchMatter, { isLoading }] = usePatchMatterMutation();

  const handleSave = async () => {
    // Basic equality check
    if (JSON.stringify(editValue) === JSON.stringify(initialValue)) {
      setIsEditing(false);
      return;
    }

    if (validate) {
      const validationResult = validate(editValue);
      if (validationResult !== true) {
        toast.error(
          typeof validationResult === 'string'
            ? validationResult
            : 'Invalid value',
        );
        return;
      }
    }

    try {
      const payload = transformValue ? transformValue(editValue) : editValue;
      await patchMatter({
        matter_id: matterId,
        patch: { [fieldName]: payload },
      }).unwrap();

      toast.success(onSuccessMessage);
      setIsEditing(false);
    } catch (error) {
      toast.error(`Failed to update`);
    }
  };

  const handleCancel = () => {
    setEditValue(initialValue);
    setIsEditing(false);
  };

  return {
    isEditing,
    setIsEditing,
    editValue,
    setEditValue,
    handleSave,
    handleCancel,
    isLoading,
  };
};
